import { Text, defaultTheme } from '@agro-club/agroclub-shared'
import env from 'env'
import useFormatDate from 'hooks/useFormatDate'
import { CurrencyFlags, CurrencySign, Export, HedgeType } from 'modules/domain/export/types'
import formatPrice from 'modules/utils/numbers/formatPrice'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Card } from 'views/components/Card/Card'

const currency = env.REACT_APP_CURRENCY_SYMBOL

const RatesBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px 0;
`
const Currencies = styled.div`
  margin-top: 4px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
`
const CurrencyBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 0 8px;
`

const NetRateCard = styled(Card.Container)`
  flex-direction: row;
  justify-content: space-between;
`

type Props = {
  exportData: Export
}

enum TypeRates {
  activation = 'activation',
  creation = 'creation',
}

const StyledText = styled(Text)<{ rateChange: 'increase' | 'decrease' | 'noChange' }>`
  color: ${({ rateChange }) => {
    if (rateChange === 'increase') return defaultTheme.color.accentApprove600
    if (rateChange === 'decrease') return defaultTheme.color.accentDestruct600
    return 'inherit'
  }};
`

export const Rates: FC<Props> = ({ exportData }) => {
  const { t } = useTranslation('export')
  const formatDate = useFormatDate()

  const renderCard = (rates, label, typeRate) => {
    const isActivation = typeRate === TypeRates.activation
    return (
      !!rates.length && (
        <Card.Container>
          <Text typography="bodyMedium">{label}</Text>
          <Currencies>
            {rates.map((item, i) => {
              const Icon = CurrencyFlags[item.currency_id]

              let rateChange: 'increase' | 'decrease' | 'noChange' = 'noChange'

              if (isActivation) {
                if (item.activation_date_rate > item.create_date_rate) {
                  rateChange = 'increase'
                } else if (item.activation_date_rate < item.create_date_rate) {
                  rateChange = 'decrease'
                }
              }

              return (
                <CurrencyBlock key={i}>
                  {Icon && <Icon />}
                  <StyledText typography="bodyMedium" rateChange={rateChange}>
                    {formatPrice(isActivation ? item.activation_date_rate : item.create_date_rate, true)}
                  </StyledText>
                </CurrencyBlock>
              )
            })}
          </Currencies>
        </Card.Container>
      )
    )
  }
  const isOptionType = exportData.hedge_type === HedgeType.option

  return (
    <RatesBlock>
      {renderCard(exportData.exchange_rates, t('creationRate'), TypeRates.creation)}
      {renderCard(
        exportData.exchange_rates,
        t('dateRate', { date: formatDate(exportData.exchange_rates[0].activation_date, { withTime: true }) }),
        TypeRates.activation,
      )}
      {!!exportData.net_rate && (
        <NetRateCard>
          <Text typography="bodyMedium">{isOptionType ? t('netRate') : t('rate')}</Text>
          <Text typography="accentMedium">
            {exportData.net_rate}{' '}
            {t('currencyPerCurrency', { currency, perCurrency: CurrencySign[exportData.hedge_currency] })}
          </Text>
        </NetRateCard>
      )}
    </RatesBlock>
  )
}
